import { IAccountActions } from "@/account/account-actions";
import type { IUserInfo } from "@/account/account-models";
import type { IDictionary } from "@/models";
import { removeDuplicates, sortByLastMessageTime } from "@/utils/array";
import { createReducer } from "@reduxjs/toolkit";
import { IChatsActions } from "./chats-actions";
import type { IChatFilter, IGetChatsConfig } from "./chats-helpers";
import { DEFAULT_CHAT_FILTERS_STATE } from "./chats-helpers";
import type { IBroadcastMessage, IChatInfo, IChatTotals } from "./chats-models";
import { EBroadcastStage } from "./chats-models";
import { IMessengerActions } from "./messenger/messenger-actions";

export interface IChatsModel {
  filtersSet: IDictionary<IChatFilter>;
  chats: IChatInfo[];
  // chatCategories: IChatCategories;
  chatTotals: IChatTotals;
  userItems: IUserInfo[];
  getChatsNextUrl: string;
  isEditing: boolean;
  isSelectAll: boolean;
  chatsSelection: string[]; // only ids
  broadcastMessages: IBroadcastMessage[];
  broadcastStage: EBroadcastStage;
}

const INITIAL_STATE: IChatsModel = {
  filtersSet: DEFAULT_CHAT_FILTERS_STATE,
  chats: [],
  // chatCategories: {
  //   all: [],
  //   online: [],
  //   followers: [],
  //   fans: [],
  //   subscribers: [],
  //   favorites: [],
  //   unreplied: [],
  //   recent: [],
  //   vip: []
  // },
  chatTotals: {
    all: 0,
    online: 0,
    fans: 0,
    paid_chat: 0,
    followers: 0,
    following: 0,
    subscribers: 0,
    subscribed: 0,
    favorites: 0,
    unreplied: 0,
    unread: 0,
    vip: 0,
    recent: 0,
    sent: 0,
    hidden: 0,
    creators: 0,
  },
  userItems: [],
  getChatsNextUrl: "",
  isEditing: false,
  isSelectAll: false,
  chatsSelection: [],
  broadcastMessages: [],
  broadcastStage: EBroadcastStage.None,
};

/** Reducer */
export const chatsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(IAccountActions.successGetUser, (state, action): IChatsModel => {
      const user = action.payload as IUserInfo;
      if (user.is_broadcasting) {
        return {
          ...state,
          broadcastStage: EBroadcastStage.Broadcasting,
        };
      } else if (state.broadcastStage === EBroadcastStage.Broadcasting) {
        // fallback in case of ws type broadcast_done failing
        return {
          ...state,
          broadcastStage: EBroadcastStage.None,
        };
      }
      return state;
    })
    .addCase(IChatsActions.setFilters, (state, action): IChatsModel => {
      return {
        ...state,
        filtersSet: {
          // ...state.filtersSet,
          ...action.payload.filtersSet,
        },
      };
    })
    .addCase(IChatsActions.initGetChats, (state, action): IChatsModel => {
      const { isInitialRequest, isSneakyReload } = action.payload
        .config as IGetChatsConfig;
      const nextState = {
        chats: isInitialRequest && !isSneakyReload ? [] : state.chats,
        // chatCategories: {
        //   ...state.chatCategories,
        //   [filtersSet[EChatFilterType.Category].value as EChatFilterCategory]:
        //     isInitialRequest && !isSneakyReload ? [] : state.chats
        // },
        userItems: isInitialRequest && !isSneakyReload ? [] : state.userItems,
      };
      return {
        ...state,
        ...nextState,
      };
    })
    .addCase(IChatsActions.successGetChats, (state, action): IChatsModel => {
      const config = action.payload.config;
      let chats: IChatInfo[] = action.payload.res.data || [];
      let userItems: IUserInfo[] = action.payload.res.data_users || [];
      const getChatsNextUrl: string = action.payload.res.next;

      if (!config.isInitialRequest) {
        chats = [...state.chats, ...chats];
        userItems = [...state.userItems, ...userItems];
      }

      chats = removeDuplicates(chats, "_id", true).sort(sortByLastMessageTime);

      const nextState = {
        getChatsNextUrl,
        chats,
        // chatCategories: {
        //   ...state.chatCategories,
        //   [config.filtersSet[EChatFilterType.Category].value as EChatFilterCategory]: chats
        // },
        userItems,
      };

      return {
        ...state,
        ...nextState,
      };
    })
    .addCase(IChatsActions.updateChatTotals, (state, action): IChatsModel => {
      return {
        ...state,
        chatTotals: {
          ...state.chatTotals,
          ...action.payload,
        },
      };
    })
    .addCase(IMessengerActions.successGetChat, (state, action): IChatsModel => {
      const updatedChat = action.payload as IChatInfo;
      return {
        ...state,
        chats: state.chats
          .map((chat) => (chat._id === updatedChat._id ? updatedChat : chat))
          .sort(sortByLastMessageTime),
        // chatCategories: Object.keys(state.chatCategories).reduce(
        //   (p, c: EChatFilterCategory) => ({
        //     ...p,
        //     [c]: state.chatCategories[c]
        //       .map((chat) => (chat._id === updatedChat._id ? updatedChat : chat))
        //       .sort(sortByLastMessageTime)
        //   }),
        //   {}
        // )
      };
    })
    .addCase(
      IMessengerActions.successGetChatReload,
      (state, action): IChatsModel => {
        const updatedChat = action.payload as IChatInfo;
        return {
          ...state,
          chats: state.chats
            .map((chat) => (chat._id === updatedChat._id ? updatedChat : chat))
            .sort(sortByLastMessageTime),
          // chatCategories: Object.keys(state.chatCategories).reduce(
          //   (p, c: EChatFilterCategory) => ({
          //     ...p,
          //     [c]: state.chatCategories[c]
          //       .map((chat) => (chat._id === updatedChat._id ? updatedChat : chat))
          //       .sort(sortByLastMessageTime)
          //   }),
          //   {}
          // )
        };
      }
    )
    .addCase(IMessengerActions.updateChatById, (state, action): IChatsModel => {
      const { chat_id, newValues } = action.payload;
      return {
        ...state,
        chats: state.chats
          .map((chat) =>
            chat._id === chat_id ? { ...chat, ...newValues } : chat
          )
          .sort(sortByLastMessageTime),
        // chatCategories: Object.keys(state.chatCategories).reduce(
        //   (p, c: EChatFilterCategory) => ({
        //     ...p,
        //     [c]: state.chatCategories[c]
        //       .map((chat) => (chat._id === chat_id ? { ...chat, ...newValues } : chat))
        //       .sort(sortByLastMessageTime)
        //   }),
        //   {}
        // )
      };
    })
    .addCase(IChatsActions.addChatToChatList, (state, action): IChatsModel => {
      let chats = [...state.chats, action.payload.chat];

      chats = removeDuplicates(chats, "_id", true).sort(sortByLastMessageTime);

      return {
        ...state,
        chats,
        // chatCategories: {
        //   ...state.chatCategories,
        //   all: removeDuplicates(
        //     [...state.chatCategories.all, action.payload.chat],
        //     '_id',
        //     true
        //   ).sort(sortByLastMessageTime)
        // }
      };
    })
    .addCase(
      IChatsActions.removeChatFromChatList,
      (state, action): IChatsModel => {
        return {
          ...state,
          chats: state.chats.filter(
            (chat) => chat.other_user._id !== action.payload.otherUserId
          ),
          // chatCategories: Object.keys(state.chatCategories).reduce(
          //   (p, c: EChatFilterCategory) => ({
          //     ...p,
          //     [c]: state.chatCategories[c].filter(
          //       (chat) => chat.other_user._id !== action.payload.otherUserId
          //     )
          //   }),
          //   {}
          // )
        };
      }
    )
    .addCase(IChatsActions.setChatEditing, (state, action): IChatsModel => {
      return {
        ...state,
        isEditing: action.payload,
        isSelectAll: false, // reset
        chatsSelection: [], // reset
      };
    })
    .addCase(
      IChatsActions.toggleChatSelection,
      (state, action): IChatsModel => {
        let chatsSelection: string[];
        if (state.chatsSelection.indexOf(action.payload) !== -1) {
          chatsSelection = state.chatsSelection.filter(
            (chatId) => chatId !== action.payload
          );
        } else {
          chatsSelection = [...state.chatsSelection, action.payload];
        }
        return {
          ...state,
          chatsSelection,
        };
      }
    )
    .addCase(
      IChatsActions.setAllChatsSelection,
      (state, action): IChatsModel => {
        return {
          ...state,
          isSelectAll: action.payload,
          chatsSelection: [],
        };
      }
    )
    .addCase(
      IChatsActions.toggleAllChatsSelection,
      (state, action): IChatsModel => {
        return {
          ...state,
          isSelectAll: !state.isSelectAll,
          chatsSelection: [],
        };
      }
    )
    .addCase(
      IChatsActions.successPatchUpdateChats,
      (state, action): IChatsModel => {
        return {
          ...state,
          // isEditing: false, // reset
          isSelectAll: false, // reset
          chatsSelection: [], // reset
        };
      }
    )
    .addCase(IChatsActions.setBroadcastStage, (state, action): IChatsModel => {
      const { stage } = action.payload;
      return {
        ...state,
        broadcastStage: stage,
      };
    })
    .addCase(
      IChatsActions.initPostBroadcastMessage,
      (state, action): IChatsModel => {
        const { clientSideBroadcastMessage } = action.payload;
        return {
          ...state,
          // broadcastMessages: [...state.broadcastMessages, clientSideBroadcastMessage],
          broadcastMessages: [clientSideBroadcastMessage],
        };
      }
    )
    .addCase(
      IChatsActions.updatePostBroadcastMessageProgress,
      (state, action): IChatsModel => {
        const { progress, loaded, total, __clientSideSendId } = action.payload;
        return {
          ...state,
          broadcastMessages: state.broadcastMessages.map((m) => {
            if (m.__clientSideSendId === __clientSideSendId) {
              return {
                ...m,
                media: {
                  ...m.media!,
                  uploadProgress: progress,
                  loaded,
                  total,
                },
              };
            }
            return m;
          }),
        };
      }
    )
    .addCase(
      IChatsActions.successPostBroadcastMessage,
      (state, action): IChatsModel => {
        const { clientSideBroadcastMessage } = action.payload;
        return {
          ...state,
          // broadcastMessages: state.broadcastMessages.map((m) => {
          //   // replace clientSideBroadcastMessage with API response
          //   if (m.__clientSideSendId === __clientSideSendId) {
          //     return response.data;
          //   }
          //   return m;
          // }),
          broadcastMessages: state.broadcastMessages.map((m) => {
            // replace clientSideBroadcastMessage with fake API response
            if (
              m.__clientSideSendId ===
              clientSideBroadcastMessage.__clientSideSendId
            ) {
              return clientSideBroadcastMessage;
            }
            return m;
          }),
        };
      }
    )
    .addCase(
      IChatsActions.failurePostBroadcastMessage,
      (state, action): IChatsModel => {
        const { isAbort, errorResponse, __clientSideSendId } = action.payload;

        let broadcastMessages = [...state.broadcastMessages];
        if (isAbort) {
          // remove clientSideBroadcastMessage
          broadcastMessages = broadcastMessages.filter(
            (m) => m.__clientSideSendId !== __clientSideSendId
          );
        } else {
          // update clientSideBroadcastMessage
          broadcastMessages = broadcastMessages.map((m) => {
            if (m.__clientSideSendId === __clientSideSendId) {
              return {
                ...m,
                __isUploadFailed: true,
                __errorMessage: (errorResponse && errorResponse.message) || "",
              };
            }
            return m;
          });
        }
        return {
          ...state,
          broadcastMessages,
        };
      }
    )
    .addCase(
      IChatsActions.initDeleteBroadcastMessage,
      (state, action): IChatsModel => {
        const { broadcastMessageId } = action.payload;
        return {
          ...state,
          broadcastMessages: state.broadcastMessages.map((m) => {
            if (m._id === broadcastMessageId) {
              return {
                ...m,
                __isBeingDeleted: true,
              };
            }
            return m;
          }),
        };
      }
    )
    .addCase(
      IChatsActions.successDeleteBroadcastMessage,
      (state, action): IChatsModel => {
        const { broadcastMessageId } = action.payload;
        return {
          ...state,
          broadcastMessages: state.broadcastMessages.filter(
            (m) => m._id !== broadcastMessageId
          ),
        };
      }
    )
    .addCase(
      IChatsActions.setBroadcastMessageMedia,
      (state, action): IChatsModel => {
        const { clientSideBroadcastMessage } = action.payload;
        return {
          ...state,
          broadcastMessages:
            clientSideBroadcastMessage != null
              ? [clientSideBroadcastMessage]
              : [],
        };
      }
    )
    .addCase(
      IChatsActions.successPostBroadcast,
      (state, action): IChatsModel => {
        return {
          ...state,
          broadcastMessages: [], // reset
        };
      }
    );
});
